import { gridSize, useDebounce } from '@/shared';
import { Theme } from '@emotion/react';
import { Autocomplete, Grid, SxProps, TextField } from '@mui/material';
import { Control, Controller, FieldError } from 'react-hook-form';
import './CustomAutocomplete.css';
import { useEffect, useState } from 'react';

export type CustomAutocompleteDynamicSearchProps<T> = {
  name: string;
  loadingText?: string;
  label: string;
  disabled?: boolean;

  optionsFetched: T[];
  valueKey: keyof T;
  actualValueKey?: keyof T;
  optionLabelForEdit?: string;
  isLoadingData: boolean;
  onChangeValue?: (value: any) => void;
  onChangeRawValue?: (value: T) => void;

  error: FieldError | undefined;
  helperText: React.ReactNode;
  required?: boolean;

  textFieldKey?: string;
  defaultValue: string | number | null | undefined;
  control: Control<any, any>;

  size?: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
  };
  disableClearable?: boolean;

  sxTextField?: SxProps<Theme>;
  endAdornmentInput?: React.ReactNode;

  //send with enter
  enableToSubmitWithEnter?: boolean;
  handleSubmit?: () => void;

  setWrittenText: React.Dispatch<React.SetStateAction<string>>
};

function CustomAutocompleteDynamicSearch<T>({
  name,
  optionsFetched,
  isLoadingData,
  error,
  textFieldKey,
  defaultValue,
  control,
  loadingText = 'Cargando...',
  optionLabelForEdit,
  valueKey,
  helperText,
  required = true,
  label,
  actualValueKey,
  onChangeValue,
  onChangeRawValue,
  size = gridSize,
  disabled = false,
  disableClearable = false,
  endAdornmentInput,

  //send with enter
  enableToSubmitWithEnter = false,
  handleSubmit = () => console.log("nothing happen"),
  setWrittenText
}: CustomAutocompleteDynamicSearchProps<T>) {
  const [searchingWord, setSearchingWord] = useState("");
  const debouncedSearchingWord = useDebounce(searchingWord,600)
  useEffect(() => {
    setWrittenText(debouncedSearchingWord)
  }, [debouncedSearchingWord]);

  return (
    <Grid item {...size}>
      <Controller

        name={name}
        control={control}
        key={textFieldKey ?? defaultValue ?? 'default'}
        defaultValue={defaultValue ?? ''}
        render={({ field }) => {
          const onChange = (_event: any, data: any) => {
            const selectedValue = data?.[actualValueKey ?? valueKey] ?? '';
            field.onChange(selectedValue);
            onChangeValue && onChangeValue(selectedValue); // label
            onChangeRawValue && onChangeRawValue(data); // T
          };

          const onKeyDown = (_event: any) => {
            enableToSubmitWithEnter ?
              _event.key === 'Enter' ? (
                // console.log('Enter key pressed')
                handleSubmit()
              )
                : null
              : null
          }

          return (
            <Autocomplete
              {...field}
              popupIcon={null}
              disabled={disabled}
              //freeSolo
              noOptionsText={'No hay opciones'}
              loading={isLoadingData}
              loadingText={loadingText}
              options={optionsFetched}
              disableClearable={disableClearable}
              getOptionLabel={(option: any) =>
                option?.[valueKey]?.toString() ??
                optionsFetched
                  .find(opt => opt[actualValueKey ?? valueKey] === option)
                  ?.[valueKey]?.toString() ??
                optionLabelForEdit?.toString() ??
                defaultValue ?? ''
              }
              renderInput={params => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    ...(endAdornmentInput && {
                      endAdornment: endAdornmentInput,
                    }),
                  }}
                  autoComplete='off'
                  label={label}
                  variant="outlined"
                  error={!!error}
                  helperText={helperText}
                  required={required}
                  sx={{
                    '& .css-1i3ux3q-MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled':
                    {
                      opacity: 0.8,
                      WebkitTextFillColor: 'black',
                    },
                  }}
                  onChange={(e)=>setSearchingWord(e.target.value)}
                />
              )}
              onChange={onChange}
              onKeyDown={onKeyDown}
            />
          );
        }}
      />
    </Grid>
  );
}

export default CustomAutocompleteDynamicSearch;